import { useState, useEffect } from "react";
import { getToken } from "../../services/tokenGET";

/**
 * Custom hook for managing species audio files
 * Handles fetching, uploading, and deleting species sounds
 * 
 * @param {number} selectedSpeciesId - Currently selected species ID
 * @returns {Object} Audio data and handlers
 */
export function useSpeciesAudio(selectedSpeciesId) {
    const [specieSound, setSpeciesSound] = useState(null);
    const [audioFile, setAudioFile] = useState(null);
    const [isAddAudioModalOpen, setAddAudioModalOpen] = useState(false);

    const retrieveSpecieSound = async (specieId) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/species/${specieId}/sounds`);
            
            if (!response.ok) {
                return null;
            }
    
            const sounds = await response.json();

            console.log("test ", sounds)
            const specieSound = sounds?.sounds?.[0] ?? null;
            return specieSound || null;
        } catch (error) {
            console.error("Error retrieving specie sound:", error);
            return null;
        }
    };

    const loadSpeciesSound = async (speciesId) => {
        const sound = await retrieveSpecieSound(speciesId);
        setSpeciesSound(sound);
    };

    const openAddAudioModal = () => {
        setAddAudioModalOpen(true);
    };

    const closeAddAudioModal = () => {
        setAddAudioModalOpen(false);
        setAudioFile(null);
    };

    const handleAudioFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setAudioFile(file);
        }
    };

    const handleAddSpecieSound = async () => {
        if (!selectedSpeciesId) {
            alert("Du måste spara arten först innan du kan lägga till ljudfiler.");
            closeAddAudioModal();
            return;
        }

        if (!audioFile) {
            alert("Ingen ljudfil vald");
            return;
        }

        const formData = new FormData();
        formData.append("sound_files[]", audioFile);
        formData.append("specie_id", selectedSpeciesId);

        try {
            const token = await getToken();
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/species/${selectedSpeciesId}/sounds`, {
                method: "POST",
                credentials: "include",
                headers: {
                    'X-CSRF-TOKEN': token
                },
                body: formData,
            });

            const data = await response.json();

            if (!response.ok) {
                console.error("Failed to upload audio:", data);
                alert("Problem uppstod vid uppladning av ljudspår. Se till att formatet är mp3 eller mp4 och att storleken inte överskrider 10MB.");
                closeAddAudioModal();
                return;
            }
                
            alert("Ljudspår uppladdat!");
            closeAddAudioModal();
            await loadSpeciesSound(selectedSpeciesId);
            
        } catch (error) {
            console.error("Upload failed:", error);
            alert("Ett fel uppstod vid uppladdning.");
        }
    };

    const handleDeleteSound = async () => {
        if (!window.confirm("Är du säker på att du vill radera detta ljudspår?")) {
            return;
        }

        try {
            const token = await getToken();
            if (!specieSound || !specieSound.sound_id) {
                alert("Kan inte radera ljudspår - saknar ljudspår-ID");
                return;
            }
            
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/species/${selectedSpeciesId}/sounds/${specieSound.sound_id}`, {
                method: "DELETE",
                credentials: "include",
                headers: {
                    'X-CSRF-TOKEN': token
                },
            });
    
            if (!response.ok) {
                throw new Error("Failed to delete sound");
            }
    
            setSpeciesSound(null);
            alert("Ljudspår raderat!");
        } catch (error) {
            console.error("Error deleting sound:", error);
            alert("Kunde inte radera ljudspåret.");
        }
    };

    const clearSound = () => {
        setSpeciesSound(null);
        setAudioFile(null);
    };

    return {
        specieSound,
        audioFile,
        isAddAudioModalOpen,
        loadSpeciesSound,
        openAddAudioModal,
        closeAddAudioModal,
        handleAudioFileChange,
        handleAddSpecieSound,
        handleDeleteSound,
        clearSound,
    };
}
